import React, { useState } from "react";
import {
  addDays,
  addMonths,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { de } from "date-fns/locale";
import { cn } from "../lib/utils";
import { Plan2PlateIcon } from "./Plan2PlateIcon";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";

export type Plan2PlateDatepickerProps = {
  date: Date;
  updateDate: (date: Date) => void;
  dataCy?: string;
  minDate?: Date;
};

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export function Plan2PlateDatepicker({
  date,
  updateDate,
  dataCy,
  minDate,
}: Plan2PlateDatepickerProps) {
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(date));

  const gridStart = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1 });
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));

  const isDisabled = (day: Date) =>
    minDate != null && isBefore(day, minDate);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        data-cy={dataCy}
        onClick={() => setOpen((o) => !o)}
        className="flex h-10 w-[135px] items-center gap-2 rounded-card border border-line bg-surface-card px-3 text-left text-sm text-ink focus:border-teal focus:outline-none"
      >
        <span className="flex-1">{format(date, "dd.MM.yyyy")}</span>
        <Plan2PlateIcon icon="calendar" size="sm" />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => setOpen(false)}
          />
          <div
            className="absolute left-0 top-full z-[9999] mt-1 w-[280px] rounded-card border border-line bg-surface-card p-3 shadow-card"
          >
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                aria-label="Vorheriger Monat"
                onClick={() => setViewMonth((m) => subMonths(m, 1))}
                className="inline-flex h-8 w-8 items-center justify-center rounded-control text-ink-muted hover:bg-surface"
              >
                <FaChevronLeft />
              </button>
              <span className="text-sm font-semibold text-ink">
                {format(viewMonth, "MMMM yyyy", { locale: de })}
              </span>
              <button
                type="button"
                aria-label="Nächster Monat"
                onClick={() => setViewMonth((m) => addMonths(m, 1))}
                className="inline-flex h-8 w-8 items-center justify-center rounded-control text-ink-muted hover:bg-surface"
              >
                <FaChevronRight />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-0.5 text-center">
              {WEEKDAYS.map((d) => (
                <div
                  key={d}
                  className="pb-1 text-xs font-semibold text-ink-muted"
                >
                  {d}
                </div>
              ))}
              {days.map((day, i) => {
                const selected = isSameDay(day, date);
                const outside = !isSameMonth(day, viewMonth);
                const disabled = isDisabled(day);
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      updateDate(day);
                      setOpen(false);
                    }}
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-full text-sm transition",
                      selected
                        ? "bg-gold font-bold text-white"
                        : outside
                          ? "text-ink-muted/40 hover:bg-surface"
                          : disabled
                            ? "cursor-not-allowed text-ink-muted/30"
                            : "text-ink hover:bg-surface",
                    )}
                  >
                    {format(day, "d")}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
